import { motion } from "framer-motion";

const steps = [
  {
    num: "01",
    title: "Discovery",
    desc: "We learn your business and identify automation opportunities.",
  },
  {
    num: "02",
    title: "Build",
    desc: "We design and develop your custom AI agent tailored to your workflows.",
  },
  {
    num: "03",
    title: "Launch & Optimise",
    desc: "We deploy, monitor, and continuously improve performance.",
  },
];

const HowItWorksSection = () => {
  return (
    <section className="py-14 lg:py-[140px] relative overflow-hidden">
      <div className="section-divider absolute top-0 left-0" />

      {/* Ambient glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] pointer-events-none opacity-30"
        style={{
          background: "radial-gradient(ellipse, hsla(27,95%,64%,0.15) 0%, transparent 70%)",
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-left mb-20 max-w-[620px] relative"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-[2px] bg-primary" />
            <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase font-['Montserrat']">
              Our Process
            </span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="text-3xl md:text-4xl lg:text-5xl font-normal font-['Montserrat'] leading-[1.08] tracking-tight mb-4"
          >
            How It <span className="text-gradient-orange">Works</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-[15px] text-muted-foreground font-['Montserrat'] font-light leading-relaxed"
          >
            From discovery to deployment — a simple, structured process.
          </motion.p>
        </motion.div>

        {/* Timeline */}
        <div className="grid grid-cols-1 md:grid-cols-3 relative">
          {/* Connecting line */}
          <div
            className="hidden md:block absolute top-[52px] h-px"
            style={{
              left: "calc(16.666% + 12px)",
              right: "calc(16.666% + 12px)",
              background: "linear-gradient(90deg, hsl(var(--primary)), hsla(27,95%,64%,0.3), hsl(var(--primary)))",
            }}
          />

          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.55 + i * 0.2, duration: 0.7 }}
              className="group relative px-0 md:px-8 max-w-[400px] md:max-w-none mx-auto"
            >
              {/* Number circle */}
              <div className="relative z-[2] mx-auto mb-9 w-[80px] h-[80px] md:w-[104px] md:h-[104px]">
                {/* Outer ring pulse */}
                <div className="absolute -inset-2 rounded-full border border-transparent transition-[border-color] duration-500 group-hover:border-primary/[0.08]" />
                {/* Main circle */}
                <div className="w-[80px] h-[80px] md:w-[104px] md:h-[104px] rounded-full border border-white/[0.06] bg-background flex items-center justify-center relative transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:border-primary/25 group-hover:-translate-y-1 group-hover:shadow-[0_0_40px_hsla(27,95%,64%,0.08),inset_0_0_30px_hsla(27,95%,64%,0.03)]">
                  <span className="text-3xl md:text-4xl font-bold text-primary leading-none transition-transform duration-400 group-hover:scale-[1.08]">
                    {step.num}
                  </span>
                  {/* Connector dot */}
                  <div className="absolute -bottom-[9px] left-1/2 -translate-x-1/2 w-[5px] h-[5px] rounded-full bg-primary opacity-40 transition-opacity duration-400 group-hover:opacity-80" />
                </div>
              </div>

              {/* Content card */}
              <div className="step-content relative text-center py-6 px-2 md:py-8 md:px-3 rounded-2xl bg-[hsl(0,0%,7%)] border border-white/[0.06] overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:border-primary/[0.12]">
                <h3 className="text-[22px] md:text-[26px] font-normal font-['Montserrat'] mb-3 text-foreground relative z-10">
                  {step.title}
                </h3>
                <p className="text-[15px] text-muted-foreground font-['Montserrat'] font-light leading-relaxed max-w-[280px] mx-auto relative z-10">
                  {step.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
